/**
 * OPFSBlobCache — streaming file cache via Origin Private File System.
 *
 * Used by the tus upload pipeline to store large files locally without:
 * - Loading the entire file into RAM (streaming via FileSystemWritableFileStream)
 * - IndexedDB quota limits (~500MB per origin)
 * - BlobEngine/nbstore sync pipeline (which buffers everything in Uint8Array)
 *
 * OPFS provides a sandboxed filesystem with practically unlimited storage.
 * Files persist across page reloads and browser restarts.
 */

import { createOPFSWritable, type OPFSWritable } from './opfs-writable';

const OPFS_ROOT = 'sl-opfs-blob-cache';
const METADATA_SUFFIX = '.meta.json';
const DEFAULT_RANGE_CHUNK_SIZE = 4 * 1024 * 1024;

export type OPFSBlobCacheSource = 'upload' | 'download' | 'range';
export type OPFSBlobUploadState = 'pending' | 'synced';

export interface OPFSBlobCacheRange {
  start: number;
  end: number;
}

export interface OPFSBlobCacheInfo {
  key: string;
  mime: string;
  size: number;
  createdAt: string;
  source: OPFSBlobCacheSource;
  complete?: boolean;
  ranges?: OPFSBlobCacheRange[];
  cacheToken?: string;
  uploadState?: OPFSBlobUploadState;
  /**
   * Set when the bytes live as one file per chunk (ranges fetched from the
   * server); absent when the blob is one file (uploads, full downloads).
   */
  chunkSize?: number;
}

export interface OPFSBlobCacheSetOptions {
  mime?: string;
  size?: number;
  source?: OPFSBlobCacheSource;
  cacheToken?: string;
  uploadState?: OPFSBlobUploadState;
}

export interface OPFSBlobCacheRemoteOptions extends OPFSBlobCacheSetOptions {
  chunkSize?: number;
  fetchRange: (start: number, endInclusive: number) => Promise<Response | null>;
}

export interface OPFSBlobCacheCompleteOptions {
  size?: number;
  cacheToken?: string;
}

export interface OPFSBlobCacheDiagnostics {
  hits: number;
  misses: number;
  fullWrites: number;
  rangeWrites: number;
  rangeRequests: number;
  rangeBytes: number;
  remoteCompletions: number;
  errors: number;
}

export type OPFSBlobCacheUnavailableReason =
  | 'api-missing'
  | 'write-api-missing'
  | 'quota-exceeded'
  | 'security-error'
  | 'unknown-error'
  | 'opfs-error';

export type OPFSBlobCacheAvailability =
  | { available: true }
  | {
      available: false;
      reason: OPFSBlobCacheUnavailableReason;
      errorName?: string;
      message?: string;
    };

const diagnostics: OPFSBlobCacheDiagnostics = {
  hits: 0,
  misses: 0,
  fullWrites: 0,
  rangeWrites: 0,
  rangeRequests: 0,
  rangeBytes: 0,
  remoteCompletions: 0,
  errors: 0,
};

function bump(metric: keyof OPFSBlobCacheDiagnostics, by = 1): void {
  diagnostics[metric] += by;
}

/** Check once if OPFS is available in this browser context */
function isOPFSAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.storage?.getDirectory === 'function'
  );
}

function initialAvailability(): OPFSBlobCacheAvailability {
  return isOPFSAvailable()
    ? { available: true }
    : {
        available: false,
        reason: 'api-missing',
        message: 'navigator.storage.getDirectory is not available',
      };
}

function unavailableFromError(error: unknown): OPFSBlobCacheAvailability {
  const isDOMException =
    typeof DOMException !== 'undefined' && error instanceof DOMException;
  const isError = error instanceof Error;
  const errorName = isDOMException || isError ? error.name : undefined;
  const message = isDOMException || isError ? error.message : undefined;

  if (errorName === 'SecurityError' || errorName === 'NotAllowedError') {
    return { available: false, reason: 'security-error', errorName, message };
  }
  if (errorName === 'QuotaExceededError') {
    return { available: false, reason: 'quota-exceeded', errorName, message };
  }
  if (errorName === 'NotSupportedError') {
    return {
      available: false,
      reason: 'write-api-missing',
      errorName,
      message,
    };
  }
  if (errorName === 'UnknownError') {
    return { available: false, reason: 'unknown-error', errorName, message };
  }
  return { available: false, reason: 'opfs-error', errorName, message };
}

let rootPromise: Promise<FileSystemDirectoryHandle> | null = null;
let opfsAvailability = initialAvailability();
let opfsAvailable = opfsAvailability.available;

function disableOPFS(error?: unknown): void {
  opfsAvailable = false;
  opfsAvailability = error
    ? unavailableFromError(error)
    : {
        available: false,
        reason: 'opfs-error',
      };
  rootPromise = null;
}

async function getRoot(): Promise<FileSystemDirectoryHandle> {
  if (!opfsAvailable || !navigator.storage?.getDirectory) {
    throw new Error('OPFS is not available');
  }

  if (!rootPromise) {
    rootPromise = navigator.storage
      .getDirectory()
      .then(root => root.getDirectoryHandle(OPFS_ROOT, { create: true }))
      .catch(err => {
        disableOPFS(err);
        throw err;
      });
  }
  return rootPromise;
}

async function getWorkspaceDir(
  workspaceId: string
): Promise<FileSystemDirectoryHandle> {
  const root = await getRoot();
  return root.getDirectoryHandle(workspaceId, { create: true });
}

/**
 * Encode a blob key for use as an OPFS filename.
 * Base64url keys may contain characters that are fine in OPFS,
 * but we encode `=` and `/` to be safe across implementations.
 */
function encodeKey(key: string): string {
  return key.replace(/=/g, '_eq').replace(/\//g, '_sl');
}

function isReadableByteStream(
  source: File | Blob | ReadableStream<Uint8Array>
): source is ReadableStream<Uint8Array> {
  return (
    typeof ReadableStream !== 'undefined' && source instanceof ReadableStream
  );
}

function metadataKey(key: string): string {
  return `${encodeKey(key)}${METADATA_SUFFIX}`;
}

/** `.` never occurs in an encoded key, so chunk names cannot collide. */
function chunkKey(key: string, index: number): string {
  return `${encodeKey(key)}.part-${index}`;
}

function chunkCount(metadata: OPFSBlobCacheInfo): number {
  return metadata.chunkSize ? Math.ceil(metadata.size / metadata.chunkSize) : 0;
}

/** Byte length the chunk `index` of a blob of `size` bytes must have. */
function chunkLength(size: number, chunkSize: number, index: number): number {
  return Math.min(chunkSize, size - index * chunkSize);
}

async function removeChunks(
  dir: FileSystemDirectoryHandle,
  metadata: OPFSBlobCacheInfo
): Promise<void> {
  for (let index = 0; index < chunkCount(metadata); index++) {
    await dir.removeEntry(chunkKey(metadata.key, index)).catch(() => {});
  }
}

/**
 * Disk-backed parts of chunks [first, last] of a chunked entry, or null when
 * one is missing or has the wrong length. Blob parts reference the files;
 * nothing is copied into memory.
 */
async function readChunks(
  dir: FileSystemDirectoryHandle,
  metadata: OPFSBlobCacheInfo & { chunkSize: number },
  first: number,
  last: number
): Promise<File[] | null> {
  const files: File[] = [];
  for (let index = first; index <= last; index++) {
    try {
      const handle = await dir.getFileHandle(chunkKey(metadata.key, index));
      const file = await handle.getFile();
      if (file.size !== chunkLength(metadata.size, metadata.chunkSize, index)) {
        return null;
      }
      files.push(file);
    } catch {
      return null;
    }
  }
  return files;
}

function isValidRange(range: OPFSBlobCacheRange): boolean {
  return (
    Number.isSafeInteger(range.start) &&
    Number.isSafeInteger(range.end) &&
    range.start >= 0 &&
    range.end >= range.start
  );
}

function mergeRanges(ranges: OPFSBlobCacheRange[]): OPFSBlobCacheRange[] {
  const sorted = ranges
    .filter(isValidRange)
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: OPFSBlobCacheRange[] = [];
  for (const range of sorted) {
    const last = merged.at(-1);
    if (!last || range.start > last.end + 1) {
      merged.push({ ...range });
      continue;
    }
    last.end = Math.max(last.end, range.end);
  }
  return merged;
}

function completeRangesForSize(size: number): OPFSBlobCacheRange[] {
  return size > 0 ? [{ start: 0, end: size - 1 }] : [];
}

function rangesCoverSize(ranges: OPFSBlobCacheRange[], size: number): boolean {
  if (size === 0) return true;
  const [first] = mergeRanges(ranges);
  return first?.start === 0 && first.end >= size - 1;
}

/** `ranges` without the bytes of `removed`. */
function subtractRange(
  ranges: OPFSBlobCacheRange[],
  removed: OPFSBlobCacheRange
): OPFSBlobCacheRange[] {
  return mergeRanges(ranges).flatMap(range => {
    if (range.end < removed.start || range.start > removed.end) return [range];
    const kept: OPFSBlobCacheRange[] = [];
    if (range.start < removed.start)
      kept.push({ start: range.start, end: removed.start - 1 });
    if (range.end > removed.end)
      kept.push({ start: removed.end + 1, end: range.end });
    return kept;
  });
}

function isCacheTokenCompatible(
  metadata: OPFSBlobCacheInfo | null,
  cacheToken: string | undefined
): boolean {
  return !metadata || !cacheToken || metadata.cacheToken === cacheToken;
}

function isCompleteMetadataFor(
  metadata: OPFSBlobCacheInfo,
  options: OPFSBlobCacheCompleteOptions = {}
): boolean {
  const expectedSize = options.size ?? metadata.size;
  const tokenCompatible =
    isCacheTokenCompatible(metadata, options.cacheToken) ||
    (metadata.source === 'upload' &&
      !metadata.cacheToken &&
      metadata.size === expectedSize);
  return (
    Number.isSafeInteger(metadata.size) &&
    metadata.size >= 0 &&
    metadata.size === expectedSize &&
    metadata.complete === true &&
    Array.isArray(metadata.ranges) &&
    rangesCoverSize(metadata.ranges, expectedSize) &&
    tokenCompatible
  );
}

function withMetadataMime(file: File, metadata: OPFSBlobCacheInfo): File {
  if (file.type === metadata.mime) return file;

  return new File([file], file.name, {
    type: metadata.mime,
    lastModified: file.lastModified,
  });
}

function parseContentRange(value: string | null): {
  start: number;
  end: number;
  size: number;
} | null {
  if (!value) return null;
  const match = /^bytes\s+(\d+)-(\d+)\/(\d+)$/i.exec(value.trim());
  if (!match) return null;
  const start = Number(match[1]);
  const end = Number(match[2]);
  const size = Number(match[3]);
  if (
    !Number.isSafeInteger(start) ||
    !Number.isSafeInteger(end) ||
    !Number.isSafeInteger(size) ||
    start < 0 ||
    end < start ||
    size < 0
  ) {
    return null;
  }
  return { start, end, size };
}

function rangesCover(
  ranges: OPFSBlobCacheRange[],
  start: number,
  endInclusive: number
): boolean {
  if (endInclusive < start) return true;
  return mergeRanges(ranges).some(
    range => range.start <= start && range.end >= endInclusive
  );
}

/** Missing bytes of [from, toInclusive], split into fetchable chunks. */
function getMissingRanges(
  ranges: OPFSBlobCacheRange[],
  from: number,
  toInclusive: number,
  chunkSize: number
): OPFSBlobCacheRange[] {
  if (toInclusive < from) return [];
  const missing: OPFSBlobCacheRange[] = [];
  let cursor = from;
  for (const range of mergeRanges(ranges)) {
    if (range.end < cursor) continue;
    if (range.start > toInclusive) break;
    if (range.start > cursor) {
      appendChunkedRanges(missing, cursor, range.start - 1, chunkSize);
    }
    cursor = Math.max(cursor, range.end + 1);
    if (cursor > toInclusive) break;
  }
  if (cursor <= toInclusive) {
    appendChunkedRanges(missing, cursor, toInclusive, chunkSize);
  }
  return missing;
}

/** Widens [start, endExclusive) to whole chunks so neighbours reuse requests. */
function alignToChunks(
  start: number,
  endExclusive: number,
  size: number,
  chunkSize: number
): OPFSBlobCacheRange {
  const alignedStart = Math.floor(start / chunkSize) * chunkSize;
  const alignedEnd = Math.ceil(endExclusive / chunkSize) * chunkSize - 1;
  return { start: alignedStart, end: Math.min(alignedEnd, size - 1) };
}

function appendChunkedRanges(
  target: OPFSBlobCacheRange[],
  start: number,
  end: number,
  chunkSize: number
): void {
  let cursor = start;
  while (cursor <= end) {
    const chunkEnd = Math.min(end, cursor + chunkSize - 1);
    target.push({ start: cursor, end: chunkEnd });
    cursor = chunkEnd + 1;
  }
}

async function writeTextFile(
  dir: FileSystemDirectoryHandle,
  name: string,
  text: string
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await createOPFSWritable(handle);
  try {
    await writable.write(text);
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => {});
    throw error;
  }
}

async function readMetadata(
  dir: FileSystemDirectoryHandle,
  key: string
): Promise<OPFSBlobCacheInfo | null> {
  try {
    const handle = await dir.getFileHandle(metadataKey(key));
    const file = await handle.getFile();
    const data = JSON.parse(await file.text()) as OPFSBlobCacheInfo;
    return data && data.key === key ? data : null;
  } catch {
    return null;
  }
}

async function readRawFile(
  workspaceId: string,
  key: string
): Promise<File | null> {
  try {
    const dir = await getWorkspaceDir(workspaceId);
    const handle = await dir.getFileHandle(encodeKey(key));
    return await handle.getFile();
  } catch {
    return null;
  }
}

/** The whole blob as one File; a chunked entry is joined without copying. */
async function readCompleteFile(
  workspaceId: string,
  metadata: OPFSBlobCacheInfo
): Promise<File | null> {
  if (!metadata.chunkSize) return readRawFile(workspaceId, metadata.key);
  const dir = await getWorkspaceDir(workspaceId);
  const chunked = metadata as OPFSBlobCacheInfo & { chunkSize: number };
  const parts =
    metadata.size === 0
      ? []
      : await readChunks(dir, chunked, 0, chunkCount(metadata) - 1);
  if (!parts) return null;
  return new File(parts, encodeKey(metadata.key), { type: metadata.mime });
}

export const opfsBlobCache = {
  get available(): boolean {
    return opfsAvailable;
  },

  getAvailability(): OPFSBlobCacheAvailability {
    return opfsAvailable ? { available: true } : opfsAvailability;
  },

  /** Probe actual file access; persistent storage permission is independent. */
  async probeAvailability(retry = false): Promise<OPFSBlobCacheAvailability> {
    if (retry) {
      rootPromise = null;
      opfsAvailability = initialAvailability();
      opfsAvailable = opfsAvailability.available;
    }
    if (!opfsAvailable) return this.getAvailability();

    let root: FileSystemDirectoryHandle | undefined;
    let probeName: string | undefined;
    try {
      probeName = `.storage-probe-${crypto.randomUUID()}`;
      root = await getRoot();
      const handle = await root.getFileHandle(probeName, { create: true });
      const writable = await createOPFSWritable(handle);
      try {
        await writable.write(new Uint8Array([0]));
        await writable.close();
      } catch (error) {
        await writable.abort().catch(() => undefined);
        throw error;
      }
    } catch (error) {
      disableOPFS(error);
    } finally {
      // Delete only this probe, never existing cached files or site data.
      if (root && probeName) {
        await root.removeEntry(probeName).catch(() => undefined);
      }
    }
    return this.getAvailability();
  },

  getDiagnostics(): OPFSBlobCacheDiagnostics {
    return { ...diagnostics };
  },

  resetDiagnostics(): void {
    for (const key of Object.keys(
      diagnostics
    ) as (keyof OPFSBlobCacheDiagnostics)[]) {
      diagnostics[key] = 0;
    }
  },

  async has(workspaceId: string, key: string): Promise<boolean> {
    return (await this.getCompleteFile(workspaceId, key)) !== null;
  },

  async get(workspaceId: string, key: string): Promise<File | null> {
    return this.getFile(workspaceId, key);
  },

  async getFile(workspaceId: string, key: string): Promise<File | null> {
    return this.getCompleteFile(workspaceId, key);
  },

  async getCompleteFile(
    workspaceId: string,
    key: string,
    options: OPFSBlobCacheCompleteOptions = {}
  ): Promise<File | null> {
    if (!this.available) return null;
    try {
      const metadata = await this.getInfo(workspaceId, key);
      if (!metadata || !isCompleteMetadataFor(metadata, options)) {
        bump('misses');
        return null;
      }

      const file = await readCompleteFile(workspaceId, metadata);
      if (!file) {
        bump('misses');
        return null;
      }
      if (file.size !== metadata.size) {
        bump('misses');
        return null;
      }
      if (options.cacheToken && metadata.cacheToken !== options.cacheToken) {
        const dir = await getWorkspaceDir(workspaceId);
        await writeTextFile(
          dir,
          metadataKey(key),
          JSON.stringify({ ...metadata, cacheToken: options.cacheToken })
        );
      }

      bump('hits');
      return withMetadataMime(file, metadata);
    } catch {
      bump('misses');
      return null;
    }
  },

  async getInfo(
    workspaceId: string,
    key: string
  ): Promise<OPFSBlobCacheInfo | null> {
    if (!this.available) return null;
    try {
      const dir = await getWorkspaceDir(workspaceId);
      const metadata = await readMetadata(dir, key);
      return metadata;
    } catch {
      return null;
    }
  },

  async setUploadState(
    workspaceId: string,
    key: string,
    uploadState: OPFSBlobUploadState
  ): Promise<boolean> {
    if (!this.available) return false;
    try {
      const dir = await getWorkspaceDir(workspaceId);
      const metadata = await readMetadata(dir, key);
      if (!metadata?.complete) {
        return false;
      }
      await writeTextFile(
        dir,
        metadataKey(key),
        JSON.stringify({ ...metadata, uploadState })
      );
      return true;
    } catch {
      bump('errors');
      return false;
    }
  },

  async setFromBlob(
    workspaceId: string,
    key: string,
    source: File | Blob,
    options: OPFSBlobCacheSetOptions = {}
  ): Promise<boolean> {
    return await this.setFromStream(workspaceId, key, source.stream(), {
      mime: options.mime ?? source.type,
      size: options.size ?? source.size,
      source: options.source,
      cacheToken: options.cacheToken,
      uploadState: options.uploadState,
    });
  },

  /**
   * Write bytes to OPFS using streaming (no full file in RAM).
   * Metadata is committed only after the byte stream closes successfully.
   */
  async setFromStream(
    workspaceId: string,
    key: string,
    source: ReadableStream<Uint8Array>,
    options: OPFSBlobCacheSetOptions = {}
  ): Promise<boolean> {
    if (!this.available) return false;
    let writable: OPFSWritable | null = null;
    let dir: FileSystemDirectoryHandle | null = null;

    try {
      dir = await getWorkspaceDir(workspaceId);
      const previous = await readMetadata(dir, key);
      if (previous?.chunkSize) {
        // The blob becomes one file; chunks of an earlier range fill go.
        await dir.removeEntry(metadataKey(key)).catch(() => {});
        await removeChunks(dir, previous);
      }
      const handle = await dir.getFileHandle(encodeKey(key), { create: true });
      writable = await createOPFSWritable(handle);
      await source.pipeTo(writable);
      writable = null;
      const file = await handle.getFile();
      if (options.size !== undefined && file.size !== options.size) {
        await dir.removeEntry(encodeKey(key)).catch(() => {});
        throw new Error(
          `OPFS blob ${key} wrote ${file.size} bytes, expected ${options.size}`
        );
      }
      const metadata: OPFSBlobCacheInfo = {
        key,
        mime: options.mime || file.type || 'application/octet-stream',
        size: options.size ?? file.size,
        createdAt: new Date().toISOString(),
        source: options.source ?? 'download',
        complete: true,
        ranges: completeRangesForSize(options.size ?? file.size),
        cacheToken: options.cacheToken,
        uploadState: options.uploadState,
      };
      await writeTextFile(dir, metadataKey(key), JSON.stringify(metadata));
      bump('fullWrites');
      return true;
    } catch {
      // Clean up partial writes
      bump('errors');
      await writable?.abort().catch(() => {});
      await dir?.removeEntry?.(encodeKey(key)).catch(() => {});
      await dir?.removeEntry?.(metadataKey(key)).catch(() => {});
      return false;
    }
  },

  /**
   * Bytes [start, endExclusive) as a disk-backed slice, or null while any of
   * them is not cached yet. Never reads the whole file into memory.
   */
  async readRange(
    workspaceId: string,
    key: string,
    start: number,
    endExclusive: number,
    options: OPFSBlobCacheCompleteOptions = {}
  ): Promise<Blob | null> {
    if (!this.available) return null;
    try {
      const metadata = await this.getInfo(workspaceId, key);
      if (
        !metadata ||
        !isCacheTokenCompatible(metadata, options.cacheToken) ||
        (options.size !== undefined && metadata.size !== options.size) ||
        !Number.isSafeInteger(start) ||
        !Number.isSafeInteger(endExclusive) ||
        start < 0 ||
        endExclusive < start ||
        endExclusive > metadata.size ||
        !rangesCover(metadata.ranges ?? [], start, endExclusive - 1)
      ) {
        bump('misses');
        return null;
      }
      const slice = await sliceEntry(
        workspaceId,
        metadata,
        start,
        endExclusive
      );
      if (!slice) {
        bump('misses');
        return null;
      }
      bump('hits');
      return slice;
    } catch {
      bump('misses');
      return null;
    }
  },

  async ensureCompleteFromRemote(
    workspaceId: string,
    key: string,
    options: OPFSBlobCacheRemoteOptions
  ): Promise<File | null> {
    if (!this.available) return null;

    try {
      const existingCompleteFile = await this.getCompleteFile(
        workspaceId,
        key,
        {
          size: options.size,
          cacheToken: options.cacheToken,
        }
      );
      if (existingCompleteFile) return existingCompleteFile;

      const entry = await prepareRemoteEntry(this, workspaceId, key, options);
      if (entry.size === 0) {
        const cached = await this.setFromBlob(
          workspaceId,
          key,
          new Blob([], { type: options.mime }),
          {
            mime: options.mime,
            size: 0,
            source: 'download',
            cacheToken: options.cacheToken,
          }
        );
        if (!cached) return null;
        bump('remoteCompletions');
        return await this.getCompleteFile(workspaceId, key, {
          size: 0,
          cacheToken: options.cacheToken,
        });
      }

      const filled = await withRemoteFillLock(workspaceId, key, async () => {
        const current = await prepareRemoteEntry(
          this,
          workspaceId,
          key,
          options
        );
        return fillRemoteWindow(this, workspaceId, key, options, {
          ranges: current.ranges,
          size: current.size,
          window: { start: 0, end: current.size - 1 },
        });
      });
      if (!filled) return null;

      const file = await this.getCompleteFile(workspaceId, key, {
        size: options.size,
        cacheToken: options.cacheToken,
      });
      if (file) bump('remoteCompletions');
      return file;
    } catch {
      bump('errors');
      return null;
    }
  },

  /**
   * Bytes [start, endExclusive) of a remote blob as a disk-backed slice.
   * Only the missing chunks around the window are fetched and kept in OPFS;
   * the rest of the file is never downloaded.
   */
  async ensureRangeFromRemote(
    workspaceId: string,
    key: string,
    start: number,
    endExclusive: number,
    options: OPFSBlobCacheRemoteOptions & { size: number }
  ): Promise<Blob | null> {
    if (!this.available) return null;
    if (
      !Number.isSafeInteger(start) ||
      !Number.isSafeInteger(endExclusive) ||
      start < 0 ||
      endExclusive < start ||
      endExclusive > options.size
    ) {
      throw new Error(
        `Invalid OPFS range ${key}@${start}-${endExclusive}/${options.size}`
      );
    }
    const rangeOptions = { size: options.size, cacheToken: options.cacheToken };
    if (endExclusive === start) return new Blob([], { type: options.mime });

    try {
      const cached = await this.readRange(
        workspaceId,
        key,
        start,
        endExclusive,
        rangeOptions
      );
      if (cached) return cached;

      const filled = await withRemoteFillLock(workspaceId, key, async () => {
        // Re-read inside the lock: a fill that just finished may cover us.
        const entry = await prepareRemoteEntry(this, workspaceId, key, options);
        return fillRemoteWindow(this, workspaceId, key, options, {
          ranges: entry.ranges,
          size: entry.size,
          window: alignToChunks(
            start,
            endExclusive,
            entry.size,
            remoteChunkSize(options)
          ),
        });
      });
      if (!filled) return null;
      return await this.readRange(
        workspaceId,
        key,
        start,
        endExclusive,
        rangeOptions
      );
    } catch {
      bump('errors');
      return null;
    }
  },

  /**
   * Backwards-compatible write helper.
   */
  async set(
    workspaceId: string,
    key: string,
    source: File | Blob | ReadableStream<Uint8Array>,
    options: OPFSBlobCacheSetOptions = {}
  ): Promise<boolean> {
    if (isReadableByteStream(source)) {
      return await this.setFromStream(workspaceId, key, source, options);
    }
    return await this.setFromBlob(workspaceId, key, source, options);
  },

  async delete(workspaceId: string, key: string): Promise<void> {
    if (!this.available) return;
    try {
      const dir = await getWorkspaceDir(workspaceId);
      const metadata = await readMetadata(dir, key);
      await dir.removeEntry(metadataKey(key)).catch(() => {});
      await dir.removeEntry(encodeKey(key)).catch(() => {});
      if (metadata) await removeChunks(dir, metadata);
    } catch {
      // Ignore if not found
    }
  },

  /** Remove every cached blob of one workspace namespace. */
  async clearWorkspace(workspaceId: string): Promise<void> {
    if (!this.available) return;
    const root = await getRoot();
    try {
      await root.removeEntry(workspaceId, { recursive: true });
    } catch (error) {
      if ((error as DOMException).name !== 'NotFoundError') throw error;
    }
  },
};

/**
 * Remote fills of one blob run one at a time: each chunk write rewrites the
 * metadata file, and overlapping OPFS writables of one file lose writes (the
 * last close wins).
 */
const remoteFillLocks = new Map<string, Promise<unknown>>();

function withRemoteFillLock<T>(
  workspaceId: string,
  key: string,
  run: () => Promise<T>
): Promise<T> {
  const lockKey = workspaceId + '\u0000' + key;
  const previous = remoteFillLocks.get(lockKey) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(run);
  const settled = next.catch(() => undefined);
  remoteFillLocks.set(lockKey, settled);
  void settled.then(() => {
    if (remoteFillLocks.get(lockKey) === settled)
      remoteFillLocks.delete(lockKey);
  });
  return next;
}

type OPFSBlobCache = typeof opfsBlobCache;

function remoteChunkSize(options: OPFSBlobCacheRemoteOptions): number {
  return Math.max(1, options.chunkSize ?? DEFAULT_RANGE_CHUNK_SIZE);
}

/**
 * Drops cached bytes that belong to another version of the blob or that no
 * longer match their metadata, and returns what can be reused.
 */
async function prepareRemoteEntry(
  cache: OPFSBlobCache,
  workspaceId: string,
  key: string,
  options: OPFSBlobCacheRemoteOptions
): Promise<{ ranges: OPFSBlobCacheRange[]; size: number }> {
  const dir = await getWorkspaceDir(workspaceId);
  const existing = await cache.getInfo(workspaceId, key);
  const compatibleExisting = isCacheTokenCompatible(
    existing,
    options.cacheToken
  )
    ? existing
    : null;
  if (existing && !compatibleExisting) {
    await cache.delete(workspaceId, key);
  }
  // Ranges are only reused in the chunk layout of this fill; a complete
  // single-file entry is reused as is, anything else is fetched again.
  let reusableExisting = compatibleExisting;
  if (
    reusableExisting &&
    !reusableExisting.complete &&
    reusableExisting.chunkSize !== remoteChunkSize(options)
  ) {
    await cache.delete(workspaceId, key);
    reusableExisting = null;
  }
  if (reusableExisting && !reusableExisting.chunkSize) {
    try {
      const handle = await dir.getFileHandle(encodeKey(key));
      const file = await handle.getFile();
      if (file.size !== reusableExisting.size) {
        await cache.delete(workspaceId, key);
        reusableExisting = null;
      }
    } catch {
      await cache.delete(workspaceId, key);
      reusableExisting = null;
    }
  }
  return {
    ranges: reusableExisting?.ranges ?? [],
    size: options.size ?? reusableExisting?.size ?? 0,
  };
}

/**
 * Fetches the missing chunks of `window` into OPFS. True when every byte of
 * the window is cached afterwards.
 */
async function fillRemoteWindow(
  cache: OPFSBlobCache,
  workspaceId: string,
  key: string,
  options: OPFSBlobCacheRemoteOptions,
  target: {
    ranges: OPFSBlobCacheRange[];
    size: number;
    window: OPFSBlobCacheRange;
  }
): Promise<boolean> {
  const chunkSize = remoteChunkSize(options);
  let ranges = target.ranges;
  const metadata = await cache.getInfo(workspaceId, key);
  if (metadata?.chunkSize === chunkSize && target.size > 0) {
    // A chunk file can vanish (storage eviction, cleared site data) while
    // the metadata still lists it; fetch such chunks again.
    const dir = await getWorkspaceDir(workspaceId);
    const first = Math.floor(target.window.start / chunkSize);
    const last = Math.floor(target.window.end / chunkSize);
    for (let index = first; index <= last; index++) {
      const chunk = {
        start: index * chunkSize,
        end: index * chunkSize + chunkLength(target.size, chunkSize, index) - 1,
      };
      if (!rangesCover(ranges, chunk.start, chunk.end)) continue;
      const present = await readChunks(
        dir,
        metadata as OPFSBlobCacheInfo & { chunkSize: number },
        index,
        index
      );
      if (!present) ranges = subtractRange(ranges, chunk);
    }
  }
  const missing = getMissingRanges(
    ranges,
    target.window.start,
    target.window.end,
    chunkSize
  );

  for (const range of missing) {
    bump('rangeRequests');
    const response = await options.fetchRange(range.start, range.end);
    if (!response) {
      return false;
    }
    // Only a 206 with exactly the asked bytes is read; any other answer,
    // also a 200 with the whole object, is dropped unread.
    const contentRange = parseContentRange(
      response.headers.get('content-range')
    );
    if (
      response.status !== 206 ||
      !response.body ||
      !contentRange ||
      contentRange.start !== range.start ||
      contentRange.end !== range.end ||
      contentRange.size !== target.size
    ) {
      await response.body?.cancel();
      return false;
    }

    await writeRemoteChunk(workspaceId, key, range.start, response.body, {
      mime:
        options.mime ||
        response.headers.get('content-type') ||
        'application/octet-stream',
      size: target.size,
      chunkSize,
      cacheToken: options.cacheToken,
    });
  }
  return true;
}

/** Bytes [start, endExclusive) of a cached entry as a disk-backed Blob. */
async function sliceEntry(
  workspaceId: string,
  metadata: OPFSBlobCacheInfo,
  start: number,
  endExclusive: number
): Promise<Blob | null> {
  if (!metadata.chunkSize) {
    const file = await readRawFile(workspaceId, metadata.key);
    if (!file || file.size < endExclusive) return null;
    return file.slice(start, endExclusive, metadata.mime);
  }
  const chunkSize = metadata.chunkSize;
  const first = Math.floor(start / chunkSize);
  const last = Math.floor((endExclusive - 1) / chunkSize);
  const dir = await getWorkspaceDir(workspaceId);
  const parts = await readChunks(
    dir,
    metadata as OPFSBlobCacheInfo & { chunkSize: number },
    first,
    last
  );
  if (!parts) return null;
  const sliced = parts.map((part, offset) => {
    const partStart = (first + offset) * chunkSize;
    return part.slice(
      Math.max(0, start - partStart),
      Math.min(part.size, endExclusive - partStart)
    );
  });
  return new Blob(sliced, { type: metadata.mime });
}

/**
 * Writes one whole chunk of a remote blob as its own OPFS file. A fresh file
 * per chunk never copies earlier bytes (a writable with keepExistingData
 * starts from a copy of the whole file) and reserves no storage for chunks
 * that were never fetched.
 */
async function writeRemoteChunk(
  workspaceId: string,
  key: string,
  start: number,
  source: ReadableStream<Uint8Array>,
  options: {
    mime: string;
    size: number;
    chunkSize: number;
    cacheToken?: string;
  }
): Promise<void> {
  const index = Math.floor(start / options.chunkSize);
  const expectedLength = chunkLength(options.size, options.chunkSize, index);
  if (start !== index * options.chunkSize || expectedLength <= 0) {
    await source.cancel().catch(() => {});
    throw new Error(`OPFS chunk ${key}@${start} is not chunk aligned`);
  }

  const dir = await getWorkspaceDir(workspaceId);
  const handle = await dir.getFileHandle(chunkKey(key, index), {
    create: true,
  });
  let writable: OPFSWritable | null = await createOPFSWritable(handle);
  let bytesWritten = 0;
  try {
    const reader = source.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      bytesWritten += value.byteLength;
      if (bytesWritten > expectedLength) {
        await reader.cancel().catch(() => {});
        throw new Error(
          `OPFS chunk ${key}@${start} exceeded ${expectedLength} bytes`
        );
      }
      await writable.write(new Uint8Array(value));
    }
    if (bytesWritten !== expectedLength) {
      throw new Error(
        `OPFS chunk ${key}@${start} wrote ${bytesWritten} bytes, expected ${expectedLength}`
      );
    }
    await writable.close();
    writable = null;

    const previous = await readMetadata(dir, key);
    const ranges = mergeRanges([
      ...(previous?.ranges ?? []),
      { start, end: start + expectedLength - 1 },
    ]);
    const metadata: OPFSBlobCacheInfo = {
      key,
      mime: options.mime || previous?.mime || 'application/octet-stream',
      size: options.size,
      createdAt: previous?.createdAt ?? new Date().toISOString(),
      source: 'range',
      complete: rangesCoverSize(ranges, options.size),
      ranges,
      cacheToken: options.cacheToken ?? previous?.cacheToken,
      chunkSize: options.chunkSize,
    };
    await writeTextFile(dir, metadataKey(key), JSON.stringify(metadata));
    bump('rangeWrites');
    bump('rangeBytes', bytesWritten);
  } catch (error) {
    bump('errors');
    await writable?.abort().catch(() => {});
    // Only this chunk is lost; the metadata never listed it.
    await dir.removeEntry(chunkKey(key, index)).catch(() => {});
    throw error;
  }
}
